import React, { useContext } from 'react';
import { css } from '@emotion/react';
import { BodyText, StickyCell } from '@/components/CarrierAccountingOrders/blocks';
import { colors } from '@/styles';
import { formatItemLabel } from '../utils';
import { Context } from '../store';
import { Pill } from '@/components/ExpandableGrid/blocks';
import { isPlaceholderItem, PLACEHOLDER_ITEM_STATUS } from '@/utilities/forwardStocking';

import styled from '@emotion/styled';
import { Grid, TableRow, TextField, MenuItem, Tooltip, IconButton } from '@material-ui/core';
import { Warning as WarningIcon, InfoOutlined } from '@material-ui/icons';

const Row = styled(({ children, ...rest }) => <TableRow {...rest}>{children}</TableRow>)`
    cursor: pointer;

    :hover td {
        background-color: #f5f5f5;
    }

    color: ${(props) => (props.error ? '#f44336' : '#2b2b2b')};
`;

function BodyRow({ row, children, ...props }) {
    return (
        <Row
            {...props}
            css={css`
                & > td {
                    padding: 8px 0;
                    position: relative;
                    z-index: 1;
                }

                &:hover > td {
                    z-index: 2;
                }
            `}
        >
            {children}
        </Row>
    );
}

function BodyCell({ cell, span = true, callbacks = {}, isChecked, className, children, ...props }) {
    return (
        <StickyCell
            align="left"
            className={className}
            {...props}
            css={css`
                position: relative;
            `}
        >
            {span ? (
                <BodyText
                    css={css`
                        position: absolute;
                        top: 50%;
                        -webkit-transform: translateY(-50%);
                        transform: translateY(-50%);
                        ${cell?.column?.id === 'est_received_date' && `width: -webkit-fill-available;`}
                        display: flex;
                        flex-direction: row;
                        flex-wrap: wrap;
                        overflow-x: auto;
                        justify-content: flex-start;
                        align-items: center;
                        width: 100%;
                        padding-left: 30px;
                        height: 50px; /* Added more height to BodyText */
                        min-height: 40px; /* Added minimum height */
                    `}
                >
                    {cell.render('Cell', { callbacks, isChecked })}
                    {children}
                </BodyText>
            ) : (
                cell.render('Cell', { callbacks, isChecked })
            )}
        </StickyCell>
    );
}

const isAlreadyInManifest = (item, currManifestType) =>
    item.manifests?.some((manifest) => manifest?.manifest?.type === currManifestType);

// Optional partial-quantity split when adding a multi-unit item to a manifest: the chosen amount
// is manifested, the remainder stays available for a future manifest (see createManifest's
// itemsToSplit). Wired to the shared itemQuantities map on the store.
const SplitItem = ({ item, itemQuantities, setItemQuantities }) => {
    const quantityOptions = new Array(item.quantity).fill(0).map((_, idx) => item.quantity - idx);

    return (
        <Grid
            container
            direction="row"
            alignItems="center"
            css={css`
                margin-left: 10px;
                max-height: 40px;
                width: auto;
            `}
        >
            <TextField
                select
                variant="outlined"
                label="Qty"
                size="small"
                value={itemQuantities[item.item_id] || item.quantity}
                onChange={(e) =>
                    setItemQuantities((prev) => ({
                        ...prev,
                        [item.item_id]: e.target.value,
                    }))
                }
                InputLabelProps={{ shrink: true }}
                css={css`
                    .MuiInputLabel-root {
                        line-height: 1;
                    }
                `}
            >
                {quantityOptions.map((qty) => (
                    <MenuItem key={qty} value={qty}>
                        {qty}
                    </MenuItem>
                ))}
            </TextField>
            <Tooltip
                style={{ color: colors.greens.primary, marginLeft: '5px' }}
                title="Optionally split this item by quantity. The amount you choose is added to this manifest; the remainder stays available for a future manifest."
                placement="top"
            >
                <IconButton size="small">
                    <InfoOutlined />
                </IconButton>
            </Tooltip>
        </Grid>
    );
};

const PlaceholderPill = ({ item }) =>
    isPlaceholderItem(item, item.order) ? (
        <Tooltip title={PLACEHOLDER_ITEM_STATUS.title} placement="top" arrow>
            <Pill
                color={PLACEHOLDER_ITEM_STATUS.color}
                minWidth="0"
                css={css`
                    margin-left: 10px;
                `}
            >
                {PLACEHOLDER_ITEM_STATUS.label}
            </Pill>
        </Tooltip>
    ) : null;

export default function SubRowComponent({ row, style, newManifestType }) {
    const item = row.original;
    const itemAlreadyInManifest = isAlreadyInManifest(item, newManifestType);
    const {
        state: { itemQuantities = {} },
        callbacks: { setItemQuantities },
    } = useContext(Context);

    return (
        <BodyRow
            {...row.getRowProps({
                style,
            })}
            row={row}
        >
            {itemAlreadyInManifest ? (
                <Grid
                    item
                    css={css`
                        font-weight: 500;
                        margin-top: 7px;
                        padding-left: 40px;
                    `}
                >
                    {formatItemLabel(item)}
                    <PlaceholderPill item={item} />
                    <span
                        css={css`
                            color: ${colors.greys.subtitle};
                            margin-left: 10px;
                            font-style: italic;
                        `}
                    >
                        {` Quantity: ${item.quantity ?? 1}`}
                    </span>
                    {['itemDamaged', 'shortage', 'overage'].includes(item.item_exception?.type) ? (
                        <WarningIcon
                            css={css`
                                color: ${colors.oranges.primary};
                            `}
                        />
                    ) : null}
                    <span
                        css={css`
                            color: ${colors.greys.subtitle};
                            margin-left: 10px;
                        `}
                    >
                        - (Already in a manifest)
                    </span>
                </Grid>
            ) : (
                <BodyCell
                    {...row?.cells?.[0]?.getCellProps()}
                    cell={row?.cells?.[0]}
                    align={row?.cells?.[0]?.column?.align}
                    span={row?.cells?.[0]?.column?.span}
                    css={css`
                        overflow: visible;
                        height: auto !important;
                        min-height: 60px;
                        padding: 12px 0;
                        background-color: #f9f9f9;
                        border-bottom: 1px solid ${colors.greys.border};
                        width: 100%; // Ensure full width
                    `}
                >
                    <Grid
                        item
                        xs={11}
                        css={css`
                            display: flex;
                            align-items: center;
                            flex-wrap: wrap;
                        `}
                    >
                        {formatItemLabel(item)}
                        <PlaceholderPill item={item} />
                        <span
                            css={css`
                                color: ${colors.greys.subtitle};
                                margin-left: 10px;
                            `}
                        >
                            {` Quantity: ${item.quantity ?? 1}`}
                        </span>
                        {['itemDamaged', 'shortage', 'overage'].includes(item.item_exception?.type) ? (
                            <WarningIcon
                                css={css`
                                    color: ${colors.oranges.primary};
                                `}
                            />
                        ) : null}
                        {(item.quantity ?? 1) > 1 ? (
                            <SplitItem
                                item={item}
                                itemQuantities={itemQuantities}
                                setItemQuantities={setItemQuantities}
                            />
                        ) : null}
                    </Grid>
                </BodyCell>
            )}
        </BodyRow>
    );
}
